<template>
  <FormKit v-model="formData" type="form" :disabled="!step.isActive" @submit="() => {}">
    <PaymentHeader
      :titles="{
        primary: paymentTitle,
        secondary: paymentSubtitle,
      }"
      :premiumInfo="{
        monthly: props.quote?.insuranceQuoteSummary?.monthlyDeposit!,
        annual: props.quote?.insuranceQuoteSummary?.yearlyAnnualPremium!,
        frequencyLabel: paymentFrequencyPriceSuffix,
        adminFee: props.quote?.insuranceQuoteSummary
          ? props.quote?.insuranceQuoteSummary?.payByTheMonthFee / 12
          : undefined,
        discount: {
          monthly: props.quote.discounts?.totalMonthlyOnlineDiscountAmount?.value,
          annual: props.quote.discounts?.totalYearlyOnlineDiscountAmount?.value,
        },
      }"
      :stepFormInputs="step.formInputs!"
      :showPaymentFrequencyDropdown="true"
    />

    <ExpandableContent
      class="input-spacing"
      :toggleButtonExpandText="step.formInputs?.needToPayLater?.toggleButtonExpandText?.value"
      :toggleButtonCollapseText="step.formInputs?.needToPayLater?.toggleButtonCollapseText?.value"
      :dynamicExpander="false"
    >
      <template #introContent>
        <RichText :field="{ value: step.formInputs?.needToPayLater?.introContent?.value }" />
      </template>
      <template #conditionalContent>
        <RichText :field="{ value: step.formInputs?.needToPayLater?.conditionalContent?.value }" class="pt-3" />

        <button :onclick="onShowModal" type="button" class="btn btn-primary w-full md:w-max mt-5 !py-1">
          {{ step.formInputs?.payLaterModal?.modalTriggerLinkText?.value }}
        </button>
      </template>
    </ExpandableContent>

    <SectionPanel
      v-if="fields.isAutoRenewalPreferenceEnabled"
      :title="{ value: formInputData('futureRenewalTitle')?.label }"
      class="mb-6"
    >
      <template #sectionPanelContent>
        <RACQFormKit :bindings="formInputData('futureRenewal')" />

        <InformationBox
          v-if="paymentStore.selectedPaymentFrequency === PaymentFrequency.Annually && toBool(formData?.futureRenewal)"
          :type="InformationBoxTypes.INFO"
          :title="step.formInputs?.payingWithACardInfoBox?.title?.value"
          :description="step.formInputs?.payingWithACardInfoBox?.content?.value"
          class="mt-6"
        />
      </template>
    </SectionPanel>

    <SectionPanel :title="{ value: formInputData('paymentTermsAndConditionsTitle')?.label }" class="mb-6">
      <template #sectionPanelContent>
        <RACQFormKit :bindings="{ ...formInputData('paymentTermsAndConditions'), validationVisibility: 'submit' }" />
      </template>
    </SectionPanel>

    <IQPaymentError :step="step" :errors="errors"></IQPaymentError>

    <SectionPanel
      v-show="
        !paymentStore.submissionSuccessful &&
        !paymentStore.disablePaymentPanel &&
        paymentStore.termsAndConditionsAccepted
      "
      :title="{ value: step.formInputs?.paymentMethod?.SectionTitle?.value }"
    >
      <template #sectionPanelContent>
        <PaymentSection
          :key="`${paymentStore.selectedPaymentFrequency}-${formData.futureRenewal}-${paymentStore.selectedPaymentMethod}`"
          v-bind="paymentMethodFields"
        ></PaymentSection>
      </template>
    </SectionPanel>

    <ModalDialog
      :modelValue="showPayLaterModal"
      :showXCloseButton="true"
      :closeOnEsc="true"
      size="md"
      @update:modelValue="(value) => onModalClose(value)"
    >
      <div class="sticky-modal-header">
        <div class="text-xl-bold">Pay later</div>
      </div>
      <div class="tooltip-body">
        <div v-if="paymentTitle" class="text-center text-xl-bold">
          {{ paymentTitle }}
        </div>
        <div v-if="paymentSubtitle" class="text-center text-xl racq-med-grey mb-4">
          {{ paymentSubtitle }}
        </div>
        <div class="text-center">
          <DollarAmount :amount="paylaterPremium" :showStarSign="false">
            <span class="text-m ml-1">per year</span>
          </DollarAmount>
          <RichText class="text-m racq-dark-grey pt-2" :field="step.formInputs?.payLaterPriceNoOnlineDiscount?.label" />
        </div>

        <InformationBox
          v-if="showOnlineDiscountWarning"
          :type="InformationBoxTypes.WARNING"
          :description="step.formInputs?.payLaterInfoBox?.content?.value"
          class="my-6"
        />

        <div
          class="richtext"
          :class="!showOnlineDiscountWarning && 'mt-6'"
          v-html="step.formInputs?.payLaterModal?.modalBody?.value"
        ></div>

        <div class="my-6">
          <PaymentMethods
            :paymentMethods="step.formInputs?.paymentMethods?.paymentMethods"
            :showAdditionalContent="false"
            :filterPaymentMethods="false"
          ></PaymentMethods>
        </div>

        <InformationBox
          :type="InformationBoxTypes.WARNING"
          :description="step.formInputs?.payLaterPaymentDueInfoBox?.content?.value"
          class="my-6"
        />

        <RichText
          v-if="step.formInputs?.payLaterDisclaimer?.label?.value && showOnlineDiscountWarning"
          :field="step.formInputs?.payLaterDisclaimer?.label"
          class="mt-6 mb-2"
        />
      </div>
      <div class="tooltip-footer">
        <div class="w-full">
          <button :onclick="() => onSubmit(true)" type="button" class="btn btn-md btn-primary w-full">
            {{ step.formInputs?.payLaterModal?.callToActionButtonText?.value }}
          </button>
        </div>
        <div class="w-full">
          <button :onclick="() => onModalClose(false)" type="button" class="btn btn-md btn-secondary w-full mt-5">
            {{ step.formInputs?.payLaterModal?.closeButtonText?.value }}
          </button>
        </div>
      </div>
    </ModalDialog>
  </FormKit>
</template>

<script lang="ts">
export const emitSubmitQuote = 'emitSubmitQuote';
export const emitTermsAndConditionsChanged = 'emitTermsAndConditionsChanged';
</script>

<script lang="ts" setup>
// 1. Vue Core Imports
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash-es';

// 2. Third-Party Libraries
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';

// 3. Interfaces & Types
import { IFormStep, IQuote, SubmitQuote, QuoteTypeCodes, IPaymentSectionProps } from '@/interfaces';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';

// 4. Stores
import {
  useFormStore,
  useReferenceDataStore,
  usePaymentStore,
  useConfigSettingsStore,
  IQConfigSettings,
  SettingCategory,
} from '@/store';

// 5. Enums & Constants
import { PaymentTypes } from '@/enums/payment-types.enum';
import { PaymentFrequency } from '@/enums/payment-frequency';
import { JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';

// 6. Utilities & Composables
import { toBool, toCurrency } from '@/utils/convert';
import useFormInputData from '@/composables/useFormInputData';

// 7. Components
import DollarAmount from '@/components/DollarAmount/DollarAmount.vue';
import ExpandableContent from '@/components/ExpandableContent/ExpandableContent.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import IQPaymentError from '@/components/IQPaymentError/IQPaymentError.vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import PaymentMethods from '@/components/PaymentMethods/PaymentMethods.vue';
import PaymentSection from '@/components/PaymentSection/PaymentSection.vue';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import SectionPanel from '@/components/SectionPanel/SectionPanel.vue';
import PaymentHeader from '@/components/PaymentHeader/PaymentHeader.vue';

// Define props and emitters
// ------------------------------------------------------------- //
const props = defineProps<{
  step: IFormStep;
  quote: Partial<IQuote>;
  paymentTitle: string;
  paymentSubtitle: string;
  fields: {
    isAutoRenewalPreferenceEnabled: Field<any>;
  };
  errors: any[];
}>();

// Define Store
// ------------------------------------------------------------- //
const formStore = useFormStore();
const paymentStore = usePaymentStore();
const refStore = useReferenceDataStore();
const configSettingsConfigStore = useConfigSettingsStore();

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const emit = defineEmits([emitSubmitQuote, emitTermsAndConditionsChanged]);
const paymentFrequencyPriceSuffix = ref<string>('');

// Define Composables
// ------------------------------------------------------------- //
const { formInputData } = useFormInputData(props.step.formInputs!);

// Define Component state
// ------------------------------------------------------------- //
const formData = ref<Record<string, any>>({});
const insuranceType = ref<string>();
const showPayLaterModal = ref<boolean>(false);
const isAnnual = computed(() => paymentStore.selectedPaymentFrequency == PaymentFrequency.Annually);

const premium = computed(() => {
  if (isAnnual.value) {
    return props.quote?.insuranceQuoteSummary?.yearlyAnnualPremium;
  } else {
    return props.quote?.insuranceQuoteSummary?.monthlyDeposit;
  }
});

const paylaterPremium = computed(() => {
  return props.quote?.insuranceQuoteSummary?.yearlyCallCenterPremium;
});

const paymentMethodFields = computed(() => {
  let fields = cloneDeep(props.step.formInputs?.paymentMethod?.fields[0]) as IPaymentSectionProps;

  fields.AcceptPaymentTypes = filteredAcceptanceTypes();

  return fields;
});

const showOnlineDiscountWarning = computed(() => {
  return (
    props.quote.discounts?.totalYearlyOnlineDiscountAmount?.value ||
    props.quote.discounts?.totalMonthlyOnlineDiscountAmount?.value
  );
});

function filteredAcceptanceTypes() {
  let filtered = props.step.formInputs?.paymentMethod?.fields[0].AcceptPaymentTypes.filter(
    (x: any) => x.name !== 'PayLater' && x.name !== 'PayPal'
  );

  filtered = filtered.filter(
    (type: any) =>
      (paymentStore.selectedPaymentFrequency === PaymentFrequency.Annually &&
        toBool(formData.value.futureRenewal) &&
        type.name !== PaymentTypes[PaymentTypes.CreditCard]) ||
      paymentStore.selectedPaymentFrequency !== PaymentFrequency.Annually ||
      !toBool(formData.value.futureRenewal)
  );

  return filtered;
}
// Lifecycle hooks
// ------------------------------------------------------------- //
onMounted(async () => {
  paymentStore.applyTermsAndConditionsValidationFix = true;

  const submissionRetries = (await configSettingsConfigStore.getSetting(
    IQConfigSettings.PaymentSubmissionRetries,
    SettingCategory.IQSettings
  )) as number;
  paymentStore.maxRetries = submissionRetries ?? 3;
  await initialisePayment();

  formData.value.futureRenewal = (paymentStore.selectedPaymentFrequency == PaymentFrequency.Monthly).toString();
});

watch(
  () => formData.value.paymentTermsAndConditions,
  (newValue) => {
    paymentStore.disablePaymentPanel = true;
    paymentStore.termsAndConditionsAccepted = newValue;
    emit(emitTermsAndConditionsChanged);
  }
);

watch(
  () => paymentStore.selectedPaymentFrequency,
  (newVal) => {
    formData.value.futureRenewal = (newVal == PaymentFrequency.Monthly).toString();
    setPaymentPriceSuffix();
    unTickTermsAndConditions();
  }
);

watch(
  () => formData.value.futureRenewal,
  (newValue) => {
    setAutoRenewPostContent();
    unTickTermsAndConditions();
  }
);

watch(
  () => paymentStore.paymentRetryAttempts,
  async function () {
    //This is done to reset the zebra iframe so payment can be attempted again.
    await resetPaymentIframe();
    await initialisePayment();

    if (paymentStore.hasExceededMaxPaymentAttempts()) {
      formStore.setDisableAllSteps();
    }
  }
);

// Define Functions
function unTickTermsAndConditions() {
  paymentStore.termsAndConditionsAccepted = false;
  formData.value.paymentTermsAndConditions = false;

  //Reset iframe otherwise it can be blank sometimes.
  paymentStore.resetIframe();
  paymentStore.selectedPaymentMethod = paymentStore.getDefaultSelectedPaymentMethod(filteredAcceptanceTypes());

  emit(emitTermsAndConditionsChanged);
}
// ------------------------------------------------------------- //

async function initialisePayment() {
  /** Set dynamic contents */
  paymentStore.submitPaymentCallback = () => onSubmit(false);
  paymentStore.disablePaymentPanel = !formData.value.paymentTermsAndConditions;
  paymentStore.selectedRenewalPreference = props.quote.renewalPreference as boolean;

  setPaymentPriceSuffix();
  setDiscountLabel();
  setAutoRenewPostContent();
  insuranceType.value = await refStore.getInsuranceProductName(QuoteTypeCodes[props.quote.quoteType!]);

  /** Map payment details for FZ Iframe */
  paymentStore.amount = premium?.value?.toString() ?? '0';
  paymentStore.buttonText = props.step.formInputs?.paymentMethod?.fields[0]?.SubmitPaymentButtonLabel?.value;
  paymentStore.reference = props.quote?.reference ?? '';
}

function setDiscountLabel() {
  const paymentSection = props.step.formInputs?.paymentMethod?.fields[0];

  if (!paymentSection) return; // Safeguard against undefined `paymentSection`

  if (isAnnual.value) {
    paymentSection.PayLaterContent.value = paymentSection.PayLaterContent?.value.replace(
      '{{onlineDiscount}}',
      dictionary.IQ_OnlineDiscount.replace(
        '$#value#',
        toCurrency(props.quote.discounts?.totalYearlyOnlineDiscountAmount?.value)
      )
    );
  } else {
    paymentSection.PayLaterContent.value = paymentSection.PayLaterContent?.value.replace(
      '{{onlineDiscount}}',
      dictionary.IQ_OnlineDiscount.replace(
        '$#value#',
        toCurrency(props.quote.discounts?.totalMonthlyOnlineDiscountAmount?.value)
      )
    );
  }
}

function setPaymentPriceSuffix() {
  const optionsValue = props.step.formInputs?.paymentFrequencyPricePrefix?.options.value;
  if (!optionsValue) return;

  const pricePrefixes = Object.fromEntries(new URLSearchParams(optionsValue));
  const enumKey = PaymentFrequency[formData.value.paymentFrequency];

  paymentFrequencyPriceSuffix.value = pricePrefixes[enumKey] || '';
}

async function setAutoRenewPostContent() {
  if (!props.fields.isAutoRenewalPreferenceEnabled) return;

  paymentStore.selectedRenewalPreference = toBool(formData.value.futureRenewal);

  const value =
    formData.value.futureRenewal == 'true'
      ? props.step.formInputs?.futureRenewalOn?.label?.value
      : props.step.formInputs?.futureRenewalOff?.label?.value;

  // Show post content
  formStore.updateInput({
    inputId: 'futureRenewal',
    updates: {
      showPostContent: true,
      postContent: {
        value,
      },
    },
  });
}

async function resetPaymentIframe() {
  paymentStore.reference = '';
}

async function onSubmit(isPayLater?: boolean) {
  try {
    if (paymentStore.paymentRetryAttempts >= paymentStore.maxRetries) return;

    if (isPayLater) {
      paymentStore.selectedPaymentMethod = PaymentTypes.PayLater;
    }

    formStore.disableAllSteps = true;

    let submitQuote = null;

    if (isPayLater) {
      //Force to Annual for pay later and trigger update to selected payment frequency.
      paymentStore.selectedPaymentFrequency = PaymentFrequency.Annually;
      paymentStore.selectedPaymentFrequencyDisplayText = paymentStore.paymentFrequencyWording?.annual;

      submitQuote = new SubmitQuote().createSubmitQuoteUsingPayLater(PaymentFrequency.Annually);
      showPayLaterModal.value = false;
    } else {
      switch (paymentStore.selectedPaymentMethod) {
        case PaymentTypes.CreditCard:
          submitQuote = new SubmitQuote().createSubmitQuoteUsingCardPaymentDetails(paymentStore.cardDetails);
          break;
        case PaymentTypes.BankAccount:
          if (paymentStore.directDebitDetails) {
            submitQuote = new SubmitQuote().createSubmitQuoteUsingBankAccountDetails(paymentStore.directDebitDetails);
          }
          break;
      }
    }

    if (!submitQuote) {
      throw JP_GENERIC_CODE;
    }

    emit(emitSubmitQuote, submitQuote);
  } catch (jPCode: any) {
    formStore.showLoadingForCurrentStep(false);
  }
}

function onShowModal() {
  showPayLaterModal.value = true;
}

function onModalClose(e: any) {
  showPayLaterModal.value = e;
}
</script>
